import type { EventTicketSummary, TicketOrderListItem } from '@pulsify/shared/dto'
import { slugify } from '@pulsify/shared/slug'
import { Link } from '@tanstack/react-router'
import { ArrowUpRight, RefreshCw, Ticket } from 'lucide-react'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { ticketsApi } from '~/entities/ticket/api/tickets-api'
import {
  useEventTicketOrdersPage,
  useEventTicketSummary,
  useExportTicketOrders,
} from '~/entities/ticket/model/use-tickets'
import { formatEventLongDay, formatEventTimeRange } from '~/features/events/lib/event-date-format'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { NetworkError } from '~/shared/lib/rpc'
import { useNow } from '~/shared/lib/use-now'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'
import { openDocument } from '../lib/open-document'
import { countLabel } from '../lib/order-format'
import { hasFilters, ordersQuery, type TicketOrdersSearch } from '../lib/order-search'
import { type EventPhase, eventPhase } from '../lib/ticket-figures'
import { OrderActionDialog, type OrderChange, useOrderActions } from './order-actions'
import { PhaseChip } from './order-parts'
import { OrderSheet } from './order-sheet'
import { OrderCards, OrderListSkeleton, OrderPager, OrderTable } from './order-table'
import { OrderToolbar } from './order-toolbar'
import { TicketFiguresCard } from './ticket-figures-card'
import { TicketTypeBreakdown } from './ticket-type-breakdown'

const CLOCK_TICK_MS = 30 * 1000

const isRunning = (summary: EventTicketSummary) => eventPhase(summary.event, new Date()) === 'live'

interface TicketOrdersPageProps {
  eventId: string
  search: TicketOrdersSearch
  onSearchChange: (search: TicketOrdersSearch) => void
}

export function TicketOrdersPage({ eventId, search, onSearchChange }: TicketOrdersPageProps) {
  const locale = getLocale()
  const now = new Date(useNow(CLOCK_TICK_MS))
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const summary = useEventTicketSummary(businessId, eventId, { refreshWhile: isRunning })

  if (summary.isError && !summary.data) {
    const missing = summary.error instanceof NetworkError && summary.error.statusCode === 404
    return (
      <Card className="flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
        <p className={cn('text-sm', missing ? 'text-foreground' : 'text-destructive')}>
          {missing ? m.eventNotFound() : m.ticketPageLoadError()}
        </p>
        {missing ? null : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => summary.refetch()}
            disabled={summary.isFetching}
          >
            <RefreshCw className={cn(summary.isFetching && 'motion-safe:animate-spin')} />
            {m.tryAgain()}
          </Button>
        )}
      </Card>
    )
  }

  if (!summary.data) return <TicketOrdersSkeleton />

  const data = summary.data
  const phase = eventPhase(data.event, now)

  return (
    <div className="flex flex-col gap-6">
      <TicketOrdersHeader summary={data} phase={phase} eventId={eventId} />
      <TicketFiguresCard summary={data} live={phase === 'live'} locale={locale} />
      {data.types.length > 0 ? (
        <TicketTypeBreakdown summary={data} eventId={eventId} locale={locale} />
      ) : null}
      <OrdersSection
        businessId={businessId}
        eventId={eventId}
        summary={data}
        live={phase === 'live'}
        search={search}
        onSearchChange={onSearchChange}
        locale={locale}
        now={now}
      />
    </div>
  )
}

function TicketOrdersHeader({
  summary,
  phase,
  eventId,
}: {
  summary: EventTicketSummary
  phase: EventPhase
  eventId: string
}) {
  const { data: business } = useCurrentBusiness()
  const start = new Date(summary.event.startTime)
  const end = new Date(summary.event.endTime)
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex min-w-0 flex-col gap-2">
        <h1 className="font-semibold text-2xl text-foreground leading-tight tracking-[-0.025em] sm:text-[30px]">
          {m.ticketPageTitle({ event: summary.event.title })}
        </h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-muted-foreground text-sm">
          <span>
            {formatEventLongDay(start)} · {formatEventTimeRange(start, end)}
          </span>
          <PhaseChip phase={phase} />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 self-start sm:self-auto">
        {summary.sellsPaid ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              openDocument(() => ticketsApi.statementLink(business?.id ?? '', eventId))
            }
          >
            {m.earningsStatement()}
          </Button>
        ) : null}
        <Button asChild variant="outline" size="sm">
          <Link to="/dashboard/events/$eventId" params={{ eventId }}>
            {m.ticketPageOpenEvent()}
            <ArrowUpRight />
          </Link>
        </Button>
      </div>
    </div>
  )
}

interface SheetState {
  open: boolean
  order: TicketOrderListItem | null
  changedAt: number
}

function applyChange(order: TicketOrderListItem, change: OrderChange): TicketOrderListItem {
  if (order.id !== change.orderId) return order
  if (change.kind === 'refund') {
    return {
      ...order,
      status: 'refunded',
      tickets: order.tickets.map((ticket) => ({ ...ticket, status: 'refunded' })),
    }
  }
  return {
    ...order,
    tickets: order.tickets.map((ticket) =>
      ticket.id === change.ticketId ? { ...ticket, status: 'void' } : ticket,
    ),
  }
}

function OrdersSection({
  businessId,
  eventId,
  summary,
  live,
  search,
  onSearchChange,
  locale,
  now,
}: {
  businessId: string
  eventId: string
  summary: EventTicketSummary
  live: boolean
  search: TicketOrdersSearch
  onSearchChange: (search: TicketOrdersSearch) => void
  locale: string
  now: Date
}) {
  const query = ordersQuery(search)
  const orders = useEventTicketOrdersPage(businessId, eventId, query, live)
  const exportCsv = useExportTicketOrders(businessId, eventId)
  const [sheet, setSheet] = useState<SheetState>({ open: false, order: null, changedAt: 0 })
  const actions = useOrderActions(businessId, eventId, (change) =>
    setSheet((current) =>
      current.order
        ? { ...current, order: applyChange(current.order, change), changedAt: Date.now() }
        : current,
    ),
  )
  const sectionRef = useRef<HTMLElement>(null)
  const titleId = useId()
  const page = orders.data
  const total = page?.total ?? 0
  const rows = page?.orders ?? []
  const fresh = orders.dataUpdatedAt > sheet.changedAt
  const shownOrder = (fresh && rows.find((row) => row.id === sheet.order?.id)) || sheet.order

  useEffect(() => {
    if (orders.isPlaceholderData || !page) return
    if (page.orders.length === 0 && page.total > 0 && page.page > 1) {
      onSearchChange({
        ...search,
        page: Math.max(1, Math.ceil(page.total / page.pageSize)),
      })
    }
  }, [page, orders.isPlaceholderData, search, onSearchChange])

  const goToPage = (next: number) => {
    onSearchChange({ ...search, page: next > 1 ? next : undefined })
    const top = sectionRef.current?.getBoundingClientRect().top ?? 0
    if (top < 0) sectionRef.current?.scrollIntoView({ block: 'start' })
  }

  const download = () => {
    const { page: _page, ...filter } = query
    exportCsv.mutate(filter, {
      onSuccess: (blob) => {
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `tickets-${slugify(summary.event.title) || eventId}.csv`
        link.click()
        URL.revokeObjectURL(url)
      },
      onError: (error) => toast.error(getErrorMessageFromError(error)),
    })
  }

  const openOrder = (order: TicketOrderListItem) => setSheet({ open: true, order, changedAt: 0 })
  const listProps = { orders: rows, actions, onOpen: openOrder, locale, now }

  return (
    <section ref={sectionRef} aria-labelledby={titleId} className="flex scroll-mt-6 flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2
          id={titleId}
          className="font-semibold font-serif text-foreground text-xl tracking-tight"
        >
          {m.ticketPageBuyersTitle()}
        </h2>
        {summary.orderCount > 0 ? (
          <p className="text-muted-foreground text-sm tabular-nums">
            {summary.orderCount === 1
              ? m.ticketPageOrderCountOne()
              : m.ticketPageOrderCount({ count: countLabel(summary.orderCount, locale) })}
          </p>
        ) : null}
      </div>

      {summary.orderCount === 0 ? (
        <Card className="flex flex-col items-start gap-3 p-6 sm:p-8">
          <span className="flex size-10 items-center justify-center rounded-full bg-muted">
            <Ticket aria-hidden className="size-5 text-muted-foreground" />
          </span>
          <div className="flex flex-col gap-1">
            <p className="font-semibold font-serif text-foreground text-lg tracking-tight">
              {m.ticketPageEmptyTitle()}
            </p>
            <p className="text-muted-foreground text-sm">{m.ticketPageEmptyBody()}</p>
          </div>
        </Card>
      ) : (
        <>
          <OrderToolbar
            search={search}
            types={summary.types}
            onSearchChange={onSearchChange}
            onExport={download}
            exporting={exportCsv.isPending}
            canExport={total > 0}
          />
          <Card className="overflow-hidden">
            {orders.isError && !page ? (
              <ListNotice text={m.ticketPageLoadError()} tone="error">
                <Button variant="outline" size="sm" onClick={() => orders.refetch()}>
                  <RefreshCw />
                  {m.tryAgain()}
                </Button>
              </ListNotice>
            ) : page ? (
              rows.length === 0 ? (
                <ListNotice text={m.ticketPageNoMatch()}>
                  {hasFilters(search) ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onSearchChange(search.sort ? { sort: search.sort } : {})}
                    >
                      {m.ticketPageReset()}
                    </Button>
                  ) : null}
                </ListNotice>
              ) : (
                <div
                  aria-busy={orders.isPlaceholderData}
                  className={cn(
                    'transition-opacity duration-150',
                    orders.isPlaceholderData && 'opacity-60',
                  )}
                >
                  <OrderTable {...listProps} />
                  <OrderCards {...listProps} />
                </div>
              )
            ) : (
              <OrderListSkeleton />
            )}
            {page && total > 0 ? (
              <OrderPager
                page={page.page}
                pageSize={page.pageSize}
                total={total}
                locale={locale}
                onPage={goToPage}
              />
            ) : null}
          </Card>
        </>
      )}

      <OrderSheet
        order={shownOrder}
        open={sheet.open}
        onOpenChange={(open) => setSheet((current) => ({ ...current, open }))}
        actions={actions}
        locale={locale}
        now={now}
      />
      <OrderActionDialog dialog={actions.dialog} locale={locale} />
    </section>
  )
}

function ListNotice({
  text,
  tone = 'muted',
  children,
}: {
  text: string
  tone?: 'muted' | 'error'
  children?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <p className={cn('text-sm', tone === 'error' ? 'text-destructive' : 'text-muted-foreground')}>
        {text}
      </p>
      {children}
    </div>
  )
}

function TicketOrdersSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-8 w-2/3 max-w-md" />
        <Skeleton className="h-4 w-64" />
      </div>
      <Skeleton className="h-36 w-full rounded-xl" />
      <Skeleton className="h-40 w-full rounded-xl" />
      <Skeleton className="h-96 w-full rounded-xl" />
    </div>
  )
}
